import { ReactNode, useCallback } from 'react';

import { cx } from '@libs/classnames';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';
import { AnimatedNode } from '@ui/components/Animation';
import { Button } from '@ui/components/Button';
import { Card } from '@ui/components/Card';
import { UseConfirmPopoverProps } from '@ui/components/ConfirmPopover/useConfirmPopover';

import styles from './ConfirmPopover.module.css';

type Props = {
  className?: string;
  description?: ReactNode;
  onConfirm: () => void;
} & UseConfirmPopoverProps;
export function ConfirmPopover({
  className,
  description,
  onConfirm,
  isOpen,
  floatingRef,
  style,
  setIsOpen,
}: Props) {
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const outsideClickRef = useClickOutside<HTMLDivElement>(handleClose);
  useOnEscape(isOpen ? handleClose : undefined);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    onConfirm();
  }, [onConfirm, setIsOpen]);

  return (
    <AnimatedNode animation="opacity">
      {isOpen && (
        <Card
          className={cx(styles.confirm, className)}
          style={style}
          ref={(el) => {
            floatingRef(el);
            outsideClickRef.current = el;
          }}
        >
          <div className={styles.description}>{description ?? 'Вы уверены?'}</div>
          <div className={styles.buttons}>
            <Button
              variant="condensed"
              className={cx(styles.button, styles.yes)}
              onClick={handleConfirm}
            >
              Да
            </Button>
            <Button variant="condensed" className={styles.button} onClick={handleClose}>
              Нет
            </Button>
          </div>
        </Card>
      )}
    </AnimatedNode>
  );
}
